import { createEmailField, RadioFieldModel, StringFieldModel } from "@/common/commonForms";
import { makeCountryRadioFieldModel } from "@/dbot/common/commonDomain";
import { DCEvent } from "@/events/eventsDomain";
import { Language } from "@/i18n/languages";
import { isValidNorwegianNationalId } from "@/shareholderRegister/shareholders/ShareholderModel";
import { Actor, actorReferences, actorsApi, ActorType, SelectedActor } from "@/types/SelectActors";
import { ShareholderDTO } from "@/types/Shareholder";
import { norway } from "@/utils/countries";
import {
  resolveUrnReference,
  toCompanyRoleReference,
  toShareholderReference,
} from "@/utils/UrnReference";

export type InstrumentHolderStatus = "ACTIVE" | "INACTIVE";

export type InstrumentHolder = {
  instrumentHolderId: string;
  organisationNumber: string;
  shareholderId?: number;
  companyRoleId?: number;
  name: string;
  email?: string;
  joinedDate?: string;
  status: InstrumentHolderStatus;
  country?: string;
};

export type HolderType = "PERSON" | "COMPANY";

export type NewHolderForm = {
  holderType: RadioFieldModel<HolderType>;
  nationality: RadioFieldModel<{ code: string; name: string }>;
  nationalIdNumber: StringFieldModel;
  firstName: StringFieldModel;
  lastName: StringFieldModel;
  email: StringFieldModel;
  utlNumber: StringFieldModel;
  companyName: StringFieldModel;
  organisationNumber: StringFieldModel;
};

// Mirrors the backend NewHolderSpec: a holder without a shareholderId also gets
// a zero-share shareholder record built from these fields.
export type NewHolderSpec = {
  name: string;
  email?: string;
  country?: string;
  shareholderId?: number;
  companyRoleId?: number;
  isCompany?: boolean;
  firstName?: string;
  lastName?: string;
  socialSecurityNumber?: string;
  utlNumber?: string;
  companyNumber?: string;
  representative?: {
    firstName: string;
    lastName: string;
    email?: string;
    socialSecurityNumber?: string;
  };
};

export type QuickAddSuggestion = {
  actor: Actor;
  holderId?: string;
  holderName?: string;
};

const normalizedIdSet = (values: Array<string | undefined>): Set<string> =>
  new Set(values.flatMap((value) => (value ? [value.replaceAll(" ", "")] : [])));

// The new-holder form rejects organisation numbers that already belong to a
// shareholder — those are added with "Add from portfolio", reusing the
// existing record instead of retyping it.
export const newHolderForm = (
  lang: Language,
  shareholders: ShareholderDTO[] = [],
): NewHolderForm => {
  const nationality = makeCountryRadioFieldModel(lang);
  const shareholderCompanyNumbers = normalizedIdSet(shareholders.map((s) => s.companyNumber));
  return {
    holderType: new RadioFieldModel<HolderType>({
      required: true,
      value: "PERSON",
      options: ["PERSON", "COMPANY"],
      idFn: (type) => type,
      labelFn: (type) => ({ key: type === "PERSON" ? "common.person" : "company" }),
    }),
    nationality,
    nationalIdNumber: new StringFieldModel({
      validator: (value) => {
        return nationality.value.code === norway && !isValidNorwegianNationalId(value)
          ? { key: "invalidNorwegianNationalId" }
          : undefined;
      },
    }),
    firstName: new StringFieldModel({ required: true }),
    lastName: new StringFieldModel({ required: true }),
    email: createEmailField(),
    utlNumber: new StringFieldModel(),
    companyName: new StringFieldModel({ required: true }),
    organisationNumber: new StringFieldModel({
      validator: (value) =>
        shareholderCompanyNumbers.has(value.replaceAll(" ", ""))
          ? { key: "options.holder-is-existing-shareholder" }
          : undefined,
    }),
  };
};

export const setHolderNationality = (
  form: NewHolderForm,
  country: { code: string; name: string },
): void => {
  form.nationality.setValue(country);
  if (!form.nationalIdNumber.isEmpty) {
    form.nationalIdNumber.validate();
  }
};

const trimmedOrUndefined = (field: StringFieldModel): string | undefined =>
  field.inputValue.trim() || undefined;

export const newHolderSpec = (form: NewHolderForm): NewHolderSpec => {
  const firstName = form.firstName.inputValue.trim();
  const lastName = form.lastName.inputValue.trim();
  const email = trimmedOrUndefined(form.email);
  const socialSecurityNumber = trimmedOrUndefined(form.nationalIdNumber);
  const utlNumber = trimmedOrUndefined(form.utlNumber);
  const country = form.nationality.value.code;
  if (form.holderType.value === "COMPANY") {
    return {
      name: form.companyName.inputValue.trim(),
      email,
      country,
      isCompany: true,
      utlNumber,
      companyNumber: trimmedOrUndefined(form.organisationNumber),
      representative:
        firstName || lastName ? { firstName, lastName, email, socialSecurityNumber } : undefined,
    };
  }
  return {
    name: `${firstName} ${lastName}`.trim(),
    email,
    country,
    firstName,
    lastName,
    socialSecurityNumber,
    utlNumber,
  };
};

export const validateNewHolderForm = (form: NewHolderForm): boolean => {
  const fields =
    form.holderType.value === "PERSON"
      ? [form.firstName, form.lastName, form.email, form.nationalIdNumber]
      : [form.companyName, form.organisationNumber, form.email, form.nationalIdNumber];
  return fields.map((field) => field.validate()).every(Boolean);
};

// Actors already registered as holders are excluded from the portfolio picker;
// they are offered as quick-add chips instead.
export const holderPortfolioReferences = (holders: InstrumentHolder[]): string[] =>
  holders.flatMap((holder) => [
    ...(holder.shareholderId === undefined ? [] : [toShareholderReference(holder.shareholderId)]),
    ...(holder.companyRoleId === undefined ? [] : [toCompanyRoleReference(holder.companyRoleId)]),
  ]);

export const loadPortfolioActors = (
  orgNo: string,
  type: ActorType,
  onSuccess: (result: Actor[]) => DCEvent[],
  onFailure: () => DCEvent[],
): DCEvent[] => {
  switch (type) {
    case ActorType.SHAREHOLDER:
      return [actorsApi.getShareholders({ orgNo, onSuccess, onFailure })];
    case ActorType.BOARD_MEMBER:
    case ActorType.CEO:
      return [actorsApi.getCompanyRoles({ orgNo, type, onSuccess, onFailure })];
    default:
      return onSuccess([]);
  }
};

const referenceLinkage = (
  reference: string | undefined,
): Pick<NewHolderSpec, "shareholderId" | "companyRoleId"> => {
  const resolved = resolveUrnReference(reference);
  return {
    shareholderId: resolved?.type === "shareholderId" ? resolved.id : undefined,
    companyRoleId: resolved?.type === "companyroleId" ? resolved.id : undefined,
  };
};

export const portfolioHolderSpec = (actor: SelectedActor): NewHolderSpec => ({
  name: actor.name,
  email: actor.email,
  country: actor.nationality,
  ...referenceLinkage(actor.reference),
});

const actorReferenceInCompany = (actor: Actor, orgNo: string): string | undefined =>
  actor.availableActors.find((a) => a.organisationNumber === orgNo)?.reference;

export const quickAddHolderSpec = (actor: Actor, orgNo: string): NewHolderSpec => ({
  name: actor.name,
  email: actor.email,
  country: actor.nationality,
  ...referenceLinkage(actorReferenceInCompany(actor, orgNo)),
});

// This company's board members and CEO, one chip per person (the same person
// often holds both roles). The actors endpoints return roles across the whole
// portfolio, so anyone without a role reference in this company is dropped.
// Existing holders are matched by the references that the server gives for the
// person in this company.
export const quickAddSuggestions = (
  actors: Actor[],
  holders: InstrumentHolder[],
  orgNo: string,
): QuickAddSuggestion[] => {
  const holdersByReference = new Map(
    holders.flatMap((holder) => [
      ...(holder.shareholderId === undefined
        ? []
        : [[toShareholderReference(holder.shareholderId), holder] as const]),
      ...(holder.companyRoleId === undefined
        ? []
        : [[toCompanyRoleReference(holder.companyRoleId), holder] as const]),
    ]),
  );
  const suggestionsByKey = new Map<string, QuickAddSuggestion>();
  actors
    .filter((actor) => !actor.isCompany && actorReferenceInCompany(actor, orgNo) !== undefined)
    .forEach((actor) => {
      const holder = actorReferences(actor)
        .map((reference) => holdersByReference.get(reference))
        .find((h) => h !== undefined);
      const suggestion: QuickAddSuggestion =
        holder === undefined
          ? { actor }
          : { actor, holderId: holder.instrumentHolderId, holderName: holder.name };
      const key = actor.inCompany?.companyRoleIds[0]?.toString() ?? actor.name.toLowerCase();
      const existing = suggestionsByKey.get(key);
      if (existing === undefined || (existing.holderId === undefined && holder !== undefined)) {
        suggestionsByKey.set(key, suggestion);
      }
    });
  return [...suggestionsByKey.values()].sort((a, b) => a.actor.name.localeCompare(b.actor.name));
};

export const companyHolderIds = (
  holders: InstrumentHolder[],
  shareholders: Pick<ShareholderDTO, "id" | "isCompany">[],
): Set<string> => {
  const companyShareholderIds = new Set(shareholders.filter((s) => s.isCompany).map((s) => s.id));
  return new Set(
    holders
      .filter((h) => h.shareholderId !== undefined && companyShareholderIds.has(h.shareholderId))
      .map((h) => h.instrumentHolderId),
  );
};

const companyNameSuffix = /\b(AS|ASA|AB|ApS|A\/S)\.?$/i;

export const holderIcon = (name: string): "buildings" | "user-square" =>
  companyNameSuffix.test(name.trim()) ? "buildings" : "user-square";

export const holderForShareholder = (
  holders: InstrumentHolder[],
  shareholderId: number,
): InstrumentHolder | undefined => holders.find((h) => h.shareholderId === shareholderId);

// Header navigation between holders in the shareholder detail view, when the
// user arrived from the options register.
export const holderSelectorItems = (holders: InstrumentHolder[]): { id: number; name: string }[] =>
  [...holders]
    .sort((a, b) => a.name.localeCompare(b.name))
    .flatMap((holder) =>
      holder.shareholderId === undefined ? [] : [{ id: holder.shareholderId, name: holder.name }],
    );
